/**
 * `nowplaying.module.css` — the screen the product is judged by.
 * `docs/design-direction.md` §8.1, §7.2 SCRUB LOUPE, §7.3 THE PULL UP.
 *
 * `NowPlaying.tsx` renders through `<StateFrame grid="plate">`, so the `.plate` grid in
 * `app/states/states.module.css` already owns the gutter padding and the `--s6` gap
 * between this file's top-level children — this file adds ONLY what §8.1 asks for on
 * top of that grid, and nothing that duplicates it.
 *
 * **The one loud object is the dubplate** (`.halo` + `.deck`). Every class in this file
 * is therefore deliberately quiet: a hairline, a mono-caps label, a tabular numeral, or
 * pure layout. Components referenced here (`Disc`, `Halftone`, `CropPlate`, `Scrub`,
 * `Transport`, `SpecTable`, `Stamp`, …) own their own visual treatment; this file only
 * places them.
 *
 * Two mechanisms are load-bearing and are called out at their rule:
 *
 *  1. **`.dim` is the SCRUB LOUPE (§7.2).** While `data-loupe="true"` sits on `.screen`
 *     (set for the life of a scrub-bar drag), everything carrying `.dim` drops to
 *     `opacity .4` over `--t16` — the waveform, its ruler and the timecodes are the
 *     three elements the spec exempts, so they carry no `.dim` class in the JSX.
 *  2. **`.archive` sits BEHIND the content, `.bed` sits ABOVE it.** `.screen` is the
 *     shared stacking context; `.archive` (`z-index: -1`) is the halftone-filled index
 *     numeral bleeding off the right edge, and `.bed` (`--z-stamp`) is the edge-cropped
 *     stamp — both painted outside `<StateFrame>` so they can overlap it (§8.1's
 *     "behind everything" / "half-cropped by the screen edge").
 */

/* ────────────────────────────────────────────────────────────────────────────────
   Screen — the shared stacking context for the two overlays below.
   ──────────────────────────────────────────────────────────────────────────────── */

.screen {
  position: relative;
  inline-size: 100%;

  /*
   * The screen box CLIPS, and that is the only thing standing between this design
   * and a page that pans sideways. Four objects on this screen deliberately bleed
   * past their box — `.halftone::before` (`inset: -40%` + `rotate(15deg)`,
   * `texture.css`), `.halo`'s negative start margin, `.archive` at
   * `inset-inline-end: calc(-1 * var(--s6))` and `.bed` + `.stamp[data-edge]` past
   * the bottom-right corner. §6.8 asks for exactly that crop; what it does not ask
   * for is 98px of live scroll port on the shell's one scroller, which is what the
   * bleed produced (measured at 393px: `.scroll` clientWidth 378 / scrollWidth 476).
   * `overflow-x: hidden` on the scroller does NOT close it — a hidden axis is still
   * a scrollable axis, and `scrollIntoView` or a stray pan moves it. Clipping HERE,
   * at the column edge, takes `scrollWidth` down to `clientWidth` and keeps every
   * intended crop: the objects are cut by the screen edge, which is the design.
   *
   * `hidden` and not `clip`: `overflow: clip` needs Safari 16+, and an older webview
   * that drops it falls back to `auto` — strictly worse than what is here now.
   */
  overflow: hidden;
}

/* `nowplaying.module.css`'s own additions to `<StateFrame className={styles.frame}>`.
   Layout stays the grid's job (per `StateFrame`'s own contract) — this only raises the
   content above `.archive`, which paints behind it. */
.frame {
  position: relative;
  z-index: var(--z-content);

  /*
   * ONE SCREEN, NO SCROLL — the operator's requirement of 2026-09-05, measured.
   *
   * This screen stacks eight modules, so the inter-module gap is multiplied by SEVEN:
   * `--s4` spends 112px and `--s3` spends 84px. The gap was cut to `--s3` on
   * 2026-09-05 to buy those 28px back, when the transport was still below the fold.
   *
   * IT IS BACK AT `--s4` since the same day, and the 28px were paid for by a bigger
   * saving in the same edit: `.downbeat` below gives up the 44px of `--drop` that the
   * one label-less separator on this screen was spending, which the operator had
   * marked as the emptiest band on the page. Net −16px, and the air is spread evenly
   * across seven module joints instead of collected into one hole in the middle.
   * Measured after both changes: no scroll at 393×852, 360×780, 412×915 or 375×667.
   * `states.module.css` reads this through `var(--plate-gap, var(--s4))`, so the rung
   * is unchanged everywhere else.
   */
  --plate-gap: var(--s4);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Top rule + eyebrow (§8.1 ¶1).
   ──────────────────────────────────────────────────────────────────────────────── */

/* A 3px accent rule across the very top. `<hr>` is reset elsewhere to a bare block;
   this is the one place it is deliberately a stripe of ink, not a 1px hairline. */
.top {
  block-size: 3px;
  border: 0;
  margin: 0;
  background: var(--accent);
}

.eyebrow {
  margin: 0;
  text-align: start;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The stage — the dubplate (loud object) and the catalogue plate (§8.1 ¶2–3).
   ──────────────────────────────────────────────────────────────────────────────── */

.stage {
  position: relative;
  display: flex;
  justify-content: flex-start;
  /*
   * NO right padding here, deliberately. `.catalogue` below is `position: absolute`,
   * so it takes no room in this flex row and needs none reserved — and reserving it
   * anyway broke every phone: `padding-inline-end: 8.5rem` (136px) ADDS to the row,
   * and `.halo` is `flex: 0 0 auto` at `min(76vw, 320px)`, so it cannot give the
   * space back. The row then measures 0.76V + 136 − 12, which exceeds the viewport
   * for any V below 516px — i.e. all of them. The page scrolled sideways and took
   * the transport's NEXT cell and the VERSION chip off-screen with it.
   *
   * §8.1 pins the plate "top-right OVER the stage", and the disc is a circle in a
   * square, so the plate lands on the empty corner the circle never reaches.
   * Overlap is the design; a wider-than-screen page was not.
   */
  align-items: flex-start;
}

/* The loud object. `min(76vw, 320px)` square, pulled left of centre and bled past the
   gutter — the record is cut off by the screen, not framed by it. */
/*
 * The third term is the one-screen fix, and it is a HEIGHT term on a width property.
 *
 * The record is a square, so its width IS its height, and at `min(76vw, 320px)` it is
 * 299px on a 393px phone — by far the tallest module on the screen and about 38% of
 * everything Telegram gives the app. That share is fine on a tall phone and fatal on a
 * short one, because `vw` knows nothing about how much vertical room exists: the same
 * 299px record ships to a 852px iPhone and to a 667px iPhone SE.
 *
 * `--app-h` is Telegram's `viewportStableHeight` (written by the façade's
 * `syncViewport`, `tokens.css`), i.e. the real height of the app, not `100vh` — which
 * inside Telegram's webview includes the strip under Telegram's own header and would
 * hand back a number the screen does not have. `100svh` is the fallback for a plain
 * browser tab before the façade has run.
 *
 * 32% keeps the dubplate the one loud object (§12.13, §8.1: nothing else on this screen
 * is within 3× of it) while letting a short phone shrink it instead of scrolling the
 * transport off the bottom. Measured at 393×852: 299px → 246px, and the whole screen
 * comes in at 735px against Telegram's ~770.
 */
.halo {
  /*
   * THE THIRD TERM IS A REMAINDER, NOT A PERCENTAGE — revised 2026-09-05 after the
   * operator marked the empty band under the chips as space to move into.
   *
   * `calc(var(--app-h) * .32)` was a share of the screen, and a share is wrong in both
   * directions because every module BUT the record costs a fixed 471px, not a
   * proportion. Measured with this harness: at 393×852 the record was cut to 272.6px
   * while 108px of the screen stayed empty below the chips, and at 375×667 it was
   * still 34px too tall and scrolled anyway — the term shrank the record on the phone
   * that did not need it and failed on the phone that did.
   *
   * A remainder gets both right. 500px is the fixed stack (471px measured, plus a
   * beat of headroom for a fully tagged track's extra spec row and a two-line title):
   *   393×852 → 352 → the record is its full min(76vw, 320px) = 298.7, 82px to spare
   *   360×780 → 280 → 273.6, 35px to spare
   *   375×667 → 167 → 167, and the transport finally fits on an iPhone SE
   *   412×915 → 415 → 313.1
   * `--app-h` is Telegram's `viewportStableHeight` (`tokens.css`), never `100vh`, which
   * inside the webview includes the strip under Telegram's own header. `100svh` is the
   * plain-browser fallback for the frame before the façade has run.
   */
  inline-size: min(76vw, 320px, calc(var(--app-h, 100svh) - 500px));
  aspect-ratio: 1 / 1;
  flex: 0 0 auto;
  transform: translateX(-8px);
  margin-inline-start: calc(-1 * var(--s3));
}

/*
 * THE PULL UP's visible half (§7.3). `NowPlaying.tsx` writes `--angle` onto THIS node
 * at pointer rate for the life of a drag and resets it to `0deg` on release; until
 * this rule existed nothing in any stylesheet read that property, so the audio
 * scrubbed under the thumb while the record sat perfectly still — the gesture shipped
 * with its feedback missing, which is why it read as "there is no scratch".
 *
 * The individual `rotate` property, NOT `transform`: `.mount[data-needle-drop]`
 * animates `transform: scale(.94 → 1)` on this same element, and `rotate` composes
 * with it instead of clobbering it for the 500ms of a needle drop. `.disc` inside
 * keeps `bl-spin` untouched (a parent rotate composes with a child rotate), and the
 * record is frozen for the whole drag anyway (`playing={isPlaying && !dragging}`), so
 * the two rotations never fight.
 *
 * `transform-origin` is load-bearing, not decoration. `.disc` is `translateX(-8px)`
 * ("deliberately off-centre", `texture.css`) inside a mount of the same width, so the
 * default 50%/50% pivot would swing that offset around the mount's centre and the
 * record would ORBIT its own spindle by up to 16px of sway. Pivoting at the disc's
 * real centre spins it in place, which is the only reading of "the hand on the
 * platter" that is not a record falling off it.
 */
.deck {
  inline-size: 100%;
  block-size: 100%;
  rotate: var(--angle, 0deg);
  transform-origin: calc(50% - 8px) 50%;
}

/*
 * THE RECORD LEFT ITS PLACE ON A TAP — the regression of 2026-09-05, measured.
 *
 * `.halo` above gained a third term (`calc(var(--app-h) * .32)`) so a short phone
 * could shrink the record instead of scrolling the transport off the bottom. It shrank
 * the MOUNT: 298.67 → 272.63 at 393×852. It did not shrink the record, because the
 * grooves are drawn by the GLOBAL `.disc` recipe in `texture.css`, which carries its
 * own `width: min(76vw, 320px)` — and `styles/index.css` is imported last, so at equal
 * specificity (0,1,0) the global wins over `.deck`'s `inline-size: 100%`. The disc
 * stayed 298.67px inside a 272.63px mount.
 *
 * Two symptoms came out of that one 26px, and both are what the operator photographed:
 *
 *  1. `transform-origin: calc(50% - 8px) 50%` is measured on the MOUNT. It resolved to
 *     (128.3, 136.3) while the disc's real centre sat at (141.3, 149.3) — a 13px miss
 *     on BOTH axes. A rotation therefore swung the record around a pivot 18.4px off its
 *     own spindle, so it ORBITED: measured 26px of travel at 88°, up to 37px at 180°.
 *     A tap that moves a few px is enough to start it. "При тапе уезжает в другое
 *     место экрана" is exactly this.
 *  2. `.mount` is `display: inline-block`, so the line box it sits in added a 32.8px
 *     baseline strut UNDER the record — dead air that pushed the title down and was
 *     invisible in every box-model reading of the stack.
 *
 * The disc is sized FROM the mount here, and the mount is a block. `:global(.disc)` is
 * the same collision `.stage .catalogue` and `.catalogueSpec.catalogueSpec` already
 * document in this file; (0,2,0) outranks the global recipe on either emission order.
 * `.deck.deck` is doubled against `.mount` for the same reason — both are (0,1,0) CSS
 * modules and bundle emission order is not a contract.
 */
.deck.deck {
  display: block;
}

.deck :global(.disc) {
  inline-size: 100%;
  block-size: 100%;
}

/*
 * The spring back (§7.3). `endDrag` writes `--angle: 0deg` in one go, so without a
 * transition the record SNAPS home the instant the thumb lifts. The transition is
 * gated on `data-pullup` — an attribute `NowPlaying.tsx` already writes and nothing
 * read — so tracking stays 1:1 and lag-free while the finger is down, and only the
 * release is eased. `--ease-cut` is the register lock-in curve §7.3 asks for.
 * `motion.css` clamps this to 1ms under `prefers-reduced-motion`, where the drag
 * handlers are not installed at all and `--angle` therefore never leaves `0deg`.
 */
.screen[data-pullup='false'] .deck {
  transition: rotate var(--tdrop) var(--ease-cut);
}

/*
 * The 1px-ruled catalogue plate, pinned top-right over the stage.
 *
 * TWO classes, deliberately. `CropPlate` renders `cx('plate', styles.crop, className)`,
 * so this element also carries the GLOBAL `.plate` recipe, and `texture.css` declares
 * `.plate { position: relative }`. Both selectors are specificity (0,1,0), and
 * `main.tsx` imports `styles/index.css` last — so every global rule is emitted after
 * every CSS-module rule and `relative` won. The plate then sat IN FLOW as a second
 * flex item beside the 76vw record: `.stage`'s min-content became 298.67 − 12 + 120 =
 * 406.67px, and because `.frame` was `grid-template-columns: 1fr` (whose automatic
 * minimum is min-content) that width propagated to EVERY module on the screen — the
 * title clipped at the left edge, the remaining timecode and the ADDED value pushed
 * off the right, the transport no longer centred. `.stage .catalogue` is (0,2,0) and
 * outranks the global recipe; `states.module.css` already uses this exact trick for
 * `cx('disc', styles.blank)`, which is the tell that the collision is structural.
 */
.stage .catalogue {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;

  /*
   * 8.5rem, not 7.5rem. At 120px the plate has 94px of content, and with the column
   * rule below `BL-0152` needs 63.7px of it — measured in Chromium, JetBrains Mono
   * 700 at `--fs-13` with `.t-13`'s 0.1em tracking. 136px gives it 68.5px and the
   * catalogue number stops wrapping. The plate is `position: absolute`, so this costs
   * the layout nothing: it takes no room in the flex row and adds no height to the
   * stack (verified: total screen height is identical at 120px and 136px).
   */
  inline-size: 8.5rem;

  /*
   * The registration crosshair (§6.9) is 12px at `--s2` from the plate's top-right
   * corner, so it occupies y 8…20 — and `CropPlate`'s own `padding: var(--s3)` starts
   * the first row at y 12. The mark sat ON TOP of the catalogue number: the operator's
   * screenshot shows a blue ⊕ over the last digit of `BL-0139`. One extra beat of top
   * padding starts the first row at y 16 clear of the mark. The plate is absolutely
   * positioned, so this costs the stack no height at all.
   */
  padding-block-start: var(--s6);
}

/*
 * The catalogue plate's own column rule — the fix for "съехало", 2026-09-05.
 *
 * `SpecTable` renders `.spec { grid-template-columns: 8.5ch 1fr }`
 * (`primitives.module.css`), which is correct for the full-width table at the bottom
 * of this screen and wrong inside a 120px plate for a reason that is invisible in the
 * source: **`ch` resolves against the GRID CONTAINER's font**, and the container
 * inherits the 15px `--ff-ui` body face — not the 11px mono the `<dt>`s are actually
 * set in. So `8.5ch` measured **71px**, leaving 36.4px for the value, and `BL-0152`
 * (63.7px) wrapped onto two lines. The plate stood 96px tall printing a catalogue
 * number broken across a line break.
 *
 * `auto` is max-content of the widest label — `SIDE`, 33.5px — which is all the column
 * was ever for. With `--s2` instead of `--s3` between the columns the value gets
 * 68.5px and prints on one line, and the plate drops to 81px.
 *
 * `minmax(0, 1fr)` and not `1fr`: a `1fr` track's automatic minimum is min-content, so
 * one long value would push the plate wider than its own `inline-size`.
 *
 * The class is DOUBLED. `.spec` is (0,1,0) and lives in another CSS module; which of
 * two equal-specificity module rules wins depends on emission order in the bundle,
 * which no file may rely on. `components.module.css`'s `.latch.latch` uses the same
 * trick against `.chip[data-selected]`, and `states.module.css` against `cx('disc', …)`
 * — the collision is structural in this codebase, not incidental
 * (see wiki: css-modules-lose-to-global-classes).
 */
.catalogueSpec.catalogueSpec {
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: var(--s2);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Title + artist (§8.1 ¶4), and the filing bracket beside them (§8.5).
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * `[title + artist][bracket]`, and the second column is `auto` — so on a phone that
 * cannot fit the head this row is exactly as wide as it always was, and the bracket
 * takes the space the 3-line clamp was never going to use.
 *
 * The bracket is `align-self: start`, NOT stretched: a 3-line Cyrillic title makes this
 * row 120px tall and a control centred in it would float in the middle of nothing. It
 * sits on the title's first line, where the eye already is.
 */
.head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: var(--s2);
  align-items: start;
}

.headText {
  display: flex;
  flex-direction: column;
  gap: var(--s1);
  /* Without this the clamped title's intrinsic width blows the `1fr` column open and
     pushes the bracket off the plate. */
  min-inline-size: 0;
}

/* Doubled, because `index.css` is imported last and a global class of equal
   specificity would otherwise win (`wiki/pages/css-modules-lose-to-global-classes`). */
.file.file {
  align-self: start;
  justify-self: end;
}

/*
 * THE OPERATOR SPENT THIS AIR, 2026-09-05.
 *
 * §8.1 pins `--drop` (44px) of air between the head and the waveform band, and this
 * rule used to lay `calc(var(--drop) - <the grid gap>)` on top of the gap to make the
 * total come to exactly that. Shown the screen on a real phone with the transport
 * below the fold, the operator marked this band as the FIRST thing to sacrifice — it
 * is the largest single piece of pure emptiness on the screen and it buys 28px, the
 * best px-per-decision on offer. The band still gets the grid's `--plate-gap` of air
 * above it, so the head and the waveform are separated like every other pair of
 * modules; what is gone is the extra emphasis, not the separation.
 *
 * This is a deliberate deviation from §8.1's named `--drop`. The token itself is
 * untouched (`tokens.test.ts` pins it at 44px, and `DownbeatRule` still draws its
 * `--drop`-wide bar) — only this screen's use of it is spent.
 */
.bandGroup {
  margin-block-start: 0;
}

/*
 * THE EMPTIEST BAND ON THE SCREEN — the operator's second red box, 2026-09-05.
 *
 * `DownbeatRule` is §6.10's only heading decoration, and `primitives.module.css`
 * gives `.section` `margin-block-start: var(--drop)` because a HEADING sits under it.
 * This screen's instance carries no label: it is a bare separator between the
 * timecodes and the spec table. Margins on grid items neither collapse nor fold into
 * `gap`, so the band measured 16 + 44 + 1 + 16 = 77px to draw one hairline — the
 * largest single piece of emptiness on the page, and the one the operator boxed in red
 * between `-1:58` and `ADDED`.
 *
 * Zeroed here only. The rule keeps a normal module gap above and below, so it still
 * separates; what is gone is the heading air for a heading that does not exist.
 *
 * Doubled class, and NOT hung on `.dim`: `.section` is (0,1,0) in another module and
 * bundle emission order is not a contract (the reason `.spec.spec` and
 * `.catalogueSpec.catalogueSpec` above are doubled too), while `.dim` is shared with
 * the title, the artist, the spec table, the transport, the archive numeral and the
 * stamp bed — a margin reset there would leak into all six.
 */
.downbeat.downbeat {
  margin-block-start: 0;
}

/*
 * TWO LINES, NOT THREE — a deliberate deviation from §4.3, 2026-09-05.
 *
 * The title is the only module on this screen whose height comes from the CONTENT
 * rather than from the design: one line of `--fs-34` display type costs 32px and three
 * cost 97px, and that 65px swing is on its own enough to push the transport below the
 * fold on the very same phone where a shorter title fits. Every other lever on this
 * screen has already been spent to make it fit one screen (see `.frame`'s
 * `--plate-gap`, `.halo`'s height term, `.bandGroup` and `.spec`), and this is the last
 * one that costs no information: §7.2's single marquee pass measures whatever the clamp
 * actually hides and reveals the tail, so a long title is still readable in full — it
 * just resolves in time rather than in space.
 *
 * The JSX still carries `t-clamp-3` and `type.css` is untouched, so the clamp is three
 * lines everywhere else in the app (`main.tsx`'s guard copy). This rule overrides it
 * for this one title. `-webkit-line-clamp` is repeated with the standard `line-clamp`
 * because that is how `type.css` writes it and old WebKit needs the prefixed one.
 */
.title {
  margin: 0;
  text-align: start;
  overflow: hidden;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

/*
 * MARQUEE, ONCE (§7.2). The layout effect measures the clamp's hidden overflow into
 * `--marquee-shift` and sets this attribute for as long as the title is truncated;
 * `animation-iteration-count: 1` is what turns that persistent flag into a single pass
 * rather than a loop (§12.19 bans an infinite marquee outright — a crate does not
 * fidget). `steps(1)` on the hold segments keeps the two resting frames pixel-static.
 *
 * `ASSUMPTION:` a `transform` on a `-webkit-line-clamp` box panning across its own
 * clipped overflow is the mechanism `NowPlaying.tsx`'s own comment describes ("the
 * block makes one transform pass that reveals the tail"); it has not been confirmed
 * against a real long Cyrillic title on a physical Telegram webview. If the tail does
 * not visibly reveal on-device, the fix is a dedicated inner-content wrapper rather
 * than a change to this rule.
 */
.title[data-marquee='true'] {
  animation: nowplaying-marquee 1200ms 1;
}

@keyframes nowplaying-marquee {
  0%,
  20% {
    transform: translateY(0);
  }

  60% {
    transform: translateY(calc(-1 * var(--marquee-shift, 0px)));
  }

  100% {
    transform: translateY(0);
  }
}

.artist {
  margin: 0;
  color: var(--accent-quiet);
  text-align: start;
}

/* Cyrillic performer names stay sentence-case, never tracked caps (§4.3, §12.16). */
.artist.cyr {
  letter-spacing: normal;
  text-transform: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The waveform band (§8.1 ¶5). `Scrub` owns its own 56px band; this only lays out the
   `CUE` / scale row above it and the flanking timecodes below it.
   ──────────────────────────────────────────────────────────────────────────────── */

.bandHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-block-end: var(--s1);
}

.scale {
  flex: 1 1 auto;
  margin-inline-start: var(--s3);
}

.times {
  display: flex;
  justify-content: space-between;
  margin: 0;
  margin-block-start: var(--s1);
}

.elapsed {
  color: var(--text);
}

.remaining {
  color: var(--text-3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Spec table (§8.1 ¶6).
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * TWO PAIRS PER LINE — the operator's second sacrificed band, 2026-09-05.
 *
 * A fully tagged track prints five rows (RIDDIM · LABEL · YEAR · PRESS · ADDED) at
 * ~19.6px each: 94px, the third-tallest module on the screen, spent on ten short mono
 * strings in a column two thirds of which is empty. Paired up it costs 47px and reads
 * the same — this is a spec plate, not prose, and the label sits against its own value
 * either way. An odd row count leaves the last pair alone on its line, which is what a
 * printed spec plate does too.
 *
 * `auto minmax(0, 1fr)` twice, not `repeat(2, auto 1fr)`: `1fr`'s automatic minimum is
 * min-content, and `ADDED 05.09.2026` beside `RIDDIM SLENG TENG` would otherwise widen
 * the table past its column and take the screen's horizontal scroll back
 * (see wiki: nowplaying-layout-defects-2026-09-05 §3).
 *
 * Doubled class, for the same reason as `.catalogueSpec` above: `.spec` is (0,1,0) in
 * another module and bundle emission order is not a contract.
 */
.spec.spec {
  inline-size: 100%;
  grid-template-columns: auto minmax(0, 1fr) auto minmax(0, 1fr);
  column-gap: var(--s3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The archive numeral — behind everything, bleeding off the right edge (§8.1 ¶8).
   ──────────────────────────────────────────────────────────────────────────────── */

.archive {
  position: absolute;
  inset-block-start: var(--s4);
  inset-inline-end: calc(-1 * var(--s6));
  z-index: -1;
  line-height: 1;
  color: transparent;
  background-image: radial-gradient(var(--accent) 0.9px, transparent 1.1px);
  background-size: 3px 3px;
  -webkit-background-clip: text;
  background-clip: text;
  opacity: 0.5;
  pointer-events: none;
  user-select: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The stamp bed — bottom right, half-cropped by the screen edge (§8.1 ¶8).
   ──────────────────────────────────────────────────────────────────────────────── */

.bed {
  position: absolute;
  inset-block-end: calc(-1 * var(--s3));
  inset-inline-end: calc(-1 * var(--s3));
  z-index: var(--z-stamp);
}

/* ────────────────────────────────────────────────────────────────────────────────
   THE SCRUB LOUPE (§7.2): everything carrying `.dim` recedes while the scrub bar is
   being dragged, so attention narrows onto the band, its ruler and the timecodes —
   the three elements the spec exempts, and the three that therefore carry no `.dim`
   class in the JSX.
   ──────────────────────────────────────────────────────────────────────────────── */

.dim {
  transition: opacity var(--t16) ease;
}

.screen[data-loupe='true'] .dim {
  opacity: 0.4;
}

/* prefers-reduced-motion (§7.4) is handled globally in `motion.css`, which clamps every
   opacity transition to 120ms rather than removing it — an opacity crossfade is not the
   motion that rule bans. The marquee's `transform` keyframes ARE motion, so they stop
   dead under reduced motion via that same global rule (`animation-duration: 1ms`). */
